import { ShowInactiveService } from './show-inactive.service';
import { DataManager } from '@syncfusion/ej2-data';
import { Component, ViewChild } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { GridComponent, NotifyArgs, SaveEventArgs, SearchSettingsModel } from "@syncfusion/ej2-angular-grids";
import { GridConstantsFactory } from "../syncfusion/grid-constants-factory";
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { ModalHelperService } from '../modal-helper';

@Component({
    selector: 'app-base',
    template: '',
    standalone: false
})
export abstract class GridBaseComponent<T extends { [K in keyof T]: AbstractControl<any>; }> {
  @ViewChild('grid')
  public grid: GridComponent;
  public filterSettings = GridConstantsFactory.createStandardFilterSettings();
  public editSettings = GridConstantsFactory.createStandardEditSettings();
  public pageSettings = GridConstantsFactory.createStandardPageSettings();
  public gridCommands = GridConstantsFactory.createStandardGridCommands();
  public searchOptions: SearchSettingsModel;
  public gridEditForm: FormGroup<T>;
  public isActiveFilter = GridConstantsFactory.createCheckBoxFilter();

  public editDialogWidth: number = 780;

  public formatIsActive(data: any, isActiveField: string = 'active'): boolean {
    if (data && data[isActiveField]) {
      return this.translateService.instant('i18n.shared.fields.active');
    }
    return this.translateService.instant('i18n.shared.inactive');
  }

  public get showInactive(): boolean {
    // due to a bug in syncfusion we can only present a value that is only initialized to syncfusion but we want to still keep the showInactive information in the service
    if (this._showInactive == null) {
      this._showInactive = this.showInactiveService.showInactive;
    }
    return this._showInactive;
  }
  public set showInactive(value: boolean) {
    if (this.showInactiveService.showInactive !== value) {
      this.showInactiveService.showInactive = value;
      // syncfusion uses [visible] only to initialize the grid
      this.grid.getColumnByField(this.isActiveColumnName ?? 'active').visible = value;
      this.grid.refreshColumns();
    }
  }
  private _showInactive: boolean = null;

  /* data to be provided by childclass */
  public data: DataManager;
  protected isActiveColumnName: string;
  protected editDialogTitle: string = 'editDialogTitle';
  protected addDialogTitle: string = 'addDialogTitle';
  protected addIndexFindColumnName: string = 'name';
  /* data to be provided by childclass */

  protected unsubscribeSubject = new Subject();
  protected formInitialValue;
  protected idOfEditedObject: number = null;
  protected modalHelperService: ModalHelperService;

  constructor(
    protected translateService: TranslateService,
    protected showInactiveService: ShowInactiveService
  ) {
    this.modalHelperService = new ModalHelperService(this.translateService);
  }

  public onAdd() {
    this.grid.addRecord();
  }

  public onSearchTextChanged(searchText: string) {
    this.grid.search(searchText);
  }

  protected abstract initializeEditForm(data: any): void;
  protected abstract getEditDialogTitleParams(rowData: any): object;

  // override this method if a more complex method is needed to compare added item with the ones in the rows (added item does not have id set)
  protected addObjectCompare(addedObject: any, rowObject: any): boolean {
    if (this.addIndexFindColumnName) {
      return addedObject[this.addIndexFindColumnName] === rowObject[this.addIndexFindColumnName];
    }
    return false;
  }

  // override this method if different data or data structure has to be provided for add / edit
  protected getDataToSave(): any {
    return this.gridEditForm.value;
  }

  protected registerGridEditFormStatusChanges(saveBtn: any) {
    this.gridEditForm.statusChanges
      .pipe(
        startWith(this.gridEditForm.status),
        takeUntil(this.unsubscribeSubject),
      )
      .subscribe(status => {
        saveBtn.disabled = (status != 'VALID') || (JSON.stringify(this.formInitialValue) === JSON.stringify(this.gridEditForm.value));
      });
  }

  public actionComplete(args) {
    if ((args.requestType === 'beginEdit' || args.requestType === 'add')) {
      const dialog = args.dialog;
      dialog.width = this.editDialogWidth;
      dialog.header = args.requestType === 'beginEdit'
        ? this.translateService.instant(this.editDialogTitle, this.getEditDialogTitleParams(args.rowData))
        : this.translateService.instant(this.addDialogTitle);

      const saveBtn = dialog.btnObj[0];
      this.registerGridEditFormStatusChanges(saveBtn);
    } else if (args.requestType === 'save') {
      if (args.action === 'add') {
        const argz = args as NotifyArgs;
        const index = argz.rows?.findIndex((row) => this.addObjectCompare(args.data, row.data));
        if (index < 0) {
          this.grid?.clearRowSelection();
        } else {
          this.grid?.selectRow(index);
        }
      }
      else if (args.action === 'edit') {
        this.idOfEditedObject = args.rowData?.id;
        // refresh is executed automatically after add and delete
        this.grid?.refresh();
      }
    } else if (args.requestType === 'refresh' && this.idOfEditedObject) {
      const index = this.grid?.getRowIndexByPrimaryKey(this.idOfEditedObject);
      this.grid?.selectRow(index);
      this.idOfEditedObject = null;
    } else if (args.requestType === 'delete') {
      const id = (args.data && args.data.length) ? args.data[0].id : null;
      const argz = args as NotifyArgs;
      const index = argz.rows?.findIndex((row) => row.data['id'] === args.data['id']);
      if (index < 0) {
        this.grid?.clearRowSelection();
      } else {
        this.grid?.selectRow(index);
      }
    }
  }

  public actionBegin(args: SaveEventArgs) {
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      this.initializeEditForm(args.rowData);
      this.formInitialValue = this.gridEditForm.value;
    }
    else if (args.requestType === 'save') {
      if (this.gridEditForm.valid) {
        args.data = this.getDataToSave();
        this.unsubscribeSubject.next(null);
      } else {
        args.cancel = true;
      }
    } else if (args.requestType === 'cancel') {
      this.unsubscribeSubject.next(null);
    } else if (args.requestType === 'filterBeforeOpen') {
      // remove like operator from string operators
      // this is an undocumented feature so test it after version upgrades
      const fargs = args as any;
      if (fargs?.filterModel?.customFilterOperators?.stringOperator != null) {
        fargs.filterModel.customFilterOperators.stringOperator = fargs.filterModel.customFilterOperators.stringOperator.filter(op => op.value !== 'like');
      }
    }
  }

  public commandClicked(args: any): void {
    if (args.commandColumn.buttonOption.iconCss.includes('icon-trash-can')) {
      this.modalHelperService.confirm(
        'i18n.core.modal.delete.title',
        'i18n.core.modal.delete.content',
        () => this.grid.deleteRecord('sourceName', args.rowData)
      );
      args.cancel = true;
    }
  }
}
